import { LoadingButton } from '@mui/lab';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Loader, MobileTable, Modal, Table } from '../../../components';
import {
  downloadCompactionTestFileVersion,
  getCompactionTestFiles
} from '../../../fetch/compaction-tests';
import { downloadFile, formatDate, getDownloadFileName } from '../../../helpers';
import { ICompactionTestFile } from '../../../models/compaction-tests';

interface ICompactionTestHistoryModal {
  jobId: number | null;
  lotNumber?: number;
  onClose: () => void;
}

export const CompactionTestHistoryModal: FC<ICompactionTestHistoryModal> = ({
  jobId,
  lotNumber,
  onClose
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState<Record<number, boolean>>({});
  const isMobile = useMediaQuery('(max-width: 960px)');

  const handleDownload = async (fileId: number, filename: string) => {
    setDownloading(prev => ({ ...prev, [fileId]: true }));
    try {
      const response = await downloadCompactionTestFileVersion(fileId);
      downloadFile(response?.data, getDownloadFileName(response) || filename);

      enqueueSnackbar('File downloaded successfully!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error downloading file, please try again.', {
        variant: 'error'
      });
    } finally {
      setDownloading(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const { isLoading, data: fileHistory } = useQuery(
    ['compactionTestFiles', jobId],
    () => getCompactionTestFiles(jobId),
    {
      enabled: !!jobId
    }
  );

  const columns = useMemo(() => {
    return [
      {
        Header: 'File Name',
        accessor: 'fileName',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompactionTestFile } };
        }) => {
          return (
            <LoadingButton
              sx={{ textDecoration: 'underline', padding: 0 }}
              variant="text"
              loading={downloading[original.id]}
              onClick={() => handleDownload(original.id, original.fileName)}
            >
              {original.fileName}
            </LoadingButton>
          );
        }
      },
      {
        Header: 'Uploaded',
        accessor: 'uploadedAt',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompactionTestFile } };
        }) => {
          return <Typography>{formatDate(original.uploadedAt)}</Typography>;
        }
      },
      {
        Header: 'Uploaded By',
        accessor: 'uploadedBy',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICompactionTestFile } };
        }) => {
          return <Typography>{original.uploadedBy}</Typography>;
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloading]);

  return (
    <Modal
      open={!!jobId}
      title={`Lot ${lotNumber} - Compaction Test History`}
      maxWidth="md"
      onClose={onClose}
      titleMarginBottom={0.5}
    >
      {isLoading ? (
        <Grid container justifyContent="center">
          <Grid item>
            <Loader type="overlay" position="centered">
              Loading...
            </Loader>
          </Grid>
        </Grid>
      ) : (
        <Table
          columns={columns}
          data={fileHistory ?? []}
          isLoading={isLoading}
          hidePagination
          mobileProps={{
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              }))
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
      )}
    </Modal>
  );
};
